import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import RegisterOperatorModal from './Registeroperatormodal';
import EditBranchModal from './EditBranchModal';
import AdminResetPasswordModal from '../Authorisation/AdminResetPasswordModal';

interface BranchDetailsData {
  branch_id: string;
  branch_code?: string;
  institute_id: string;
  name: string;
  email?: string | null;
  contact_no?: string | null;
  license_number: string;
  license_start: string;
  license_expiry: string;
  state?: string;
  district?: string;
  village?: string;
  branch_type?: string;
  address: string;
  pincode: number;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  active_operator: {
    user_id: string;
    name: string;
    email: string;
    role: string;
    assigned_at: string;
  } | null;
  created_at: string;
}

interface CourseEnrollmentCount {
  course_id: string;
  course_key: string | null;
  course_name: string;
  course_title: string;
  enrolled: number;
}

interface CourseEnrollmentData {
  branch_id: string;
  institute_id: string;
  total_enrolled: number;
  courses: CourseEnrollmentCount[];
}

interface ApiResponse {
  success: boolean;
  data: BranchDetailsData;
}

interface BranchDetailsProps {
  branchId: string;
  onBack: () => void;
  onBranchLoad?: (branchName: string, instituteId: string) => void;
  onViewStudents?: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const BRANCH_TYPE_LABELS: Record<string, string> = {
  asc: 'Authorised Study Centre (ASC)',
  franchise: 'Franchise',
  main: 'Main Branch',
};

const getBranchTypeLabel = (type?: string) =>
  type ? (BRANCH_TYPE_LABELS[type] ?? type) : '—';

// Course pill colors — cycles through a set of distinct palettes
const COURSE_COLORS = [
  { bg: '#f0f7ff', color: '#1565c0', border: '#bbdefb', bar: '#1565c0' },
  { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', bar: '#16a34a' },
  { bg: '#fdf4ff', color: '#6b21a8', border: '#e9d5ff', bar: '#9333ea' },
  { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa', bar: '#ea580c' },
  { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', bar: '#dc2626' },
  { bg: '#f0fdfa', color: '#134e4a', border: '#99f6e4', bar: '#0d9488' },
];

// ── Skeleton shimmer ──────────────────────────────────────────────────────────
const EnrollmentSkeleton: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{
        height: '56px', borderRadius: '10px',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s infinite',
      }} />
    ))}
    <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
  </div>
);

// ── Course Enrollment Card ────────────────────────────────────────────────────
const CourseEnrollmentSection: React.FC<{ branchId: string }> = ({ branchId }) => {
  const [data, setData]       = useState<CourseEnrollmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiClient.get<{ success: boolean; data: CourseEnrollmentData }>(
          `/v1/students/course-enrollment-count?branch_id=${branchId}`
        );
        if (res.data.success) setData(res.data.data);
        else setError('Failed to load enrollment data');
      } catch (e: any) {
        setError(e.response?.data?.error || 'Failed to load enrollment data');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [branchId]);

  const maxEnrolled = data?.courses.length
    ? Math.max(...data.courses.map((c) => c.enrolled))
    : 1;

  return (
    <InfoCard title="Course Enrollment" icon="pi pi-users" subtitle="Students enrolled per course in this branch">
      {loading && <EnrollmentSkeleton />}

      {!loading && error && (
        <div style={{ padding: '16px', background: '#fff3f3', borderRadius: '10px', textAlign: 'center' }}>
          <i className="pi pi-exclamation-triangle" style={{ color: '#dc3545', fontSize: '1.2rem', marginBottom: '6px', display: 'block' }} />
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#dc3545' }}>{error}</p>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* ── Summary row ── */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Total Enrolled', value: data.total_enrolled, icon: 'pi pi-users',      bg: '#0f3d3e', color: '#d4f17e' },
              { label: 'Courses Active', value: data.courses.length,  icon: 'pi pi-book',       bg: '#1a5555', color: '#d4f17e' },
            ].map((stat) => (
              <div key={stat.label} style={{
                flex: 1, padding: '14px', background: stat.bg,
                borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ width: '36px', height: '36px', background: 'rgba(212,241,126,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={stat.icon} style={{ color: stat.color, fontSize: '1rem' }} />
                </div>
                <div>
                  <p style={{ margin: '0 0 2px 0', fontSize: '1.4rem', fontWeight: '700', color: stat.color, lineHeight: 1 }}>{stat.value}</p>
                  <p style={{ margin: 0, fontSize: '0.65rem', color: 'rgba(255,255,255,0.65)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── Per-course breakdown ── */}
          {data.courses.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', background: '#f8f9fa', borderRadius: '10px' }}>
              <i className="pi pi-inbox" style={{ fontSize: '2rem', color: '#ddd', display: 'block', marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>No enrollments found for this branch</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {data.courses.map((course, index) => {
                const palette   = COURSE_COLORS[index % COURSE_COLORS.length];
                const barWidth  = maxEnrolled > 0 ? (course.enrolled / maxEnrolled) * 100 : 0;
                const label     = course.course_key || course.course_name || 'Unknown';
                const subtitle  = course.course_title || course.course_name;

                return (
                  <div key={course.course_id} style={{
                    padding: '12px 14px',
                    background: palette.bg,
                    border: `1px solid ${palette.border}`,
                    borderRadius: '10px',
                  }}>
                    {/* Top row: label + count */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.85rem', fontWeight: '700', color: palette.color }}>{label}</p>
                        {subtitle && subtitle !== label && (
                          <p style={{ margin: 0, fontSize: '0.7rem', color: palette.color, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</p>
                        )}
                      </div>
                      <div style={{
                        flexShrink: 0, marginLeft: '12px',
                        padding: '4px 10px', background: palette.color,
                        borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '5px',
                      }}>
                        <i className="pi pi-users" style={{ fontSize: '0.65rem', color: 'white' }} />
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'white' }}>{course.enrolled}</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ height: '4px', background: `${palette.bar}22`, borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${barWidth}%`,
                        background: palette.bar, borderRadius: '2px',
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </InfoCard>
  );
};

// ── Component ──────────────────────────────────────────────────────────────────

const BranchDetails: React.FC<BranchDetailsProps> = ({ branchId, onBack, onBranchLoad, onViewStudents }) => {
  const [branch, setBranch] = useState<BranchDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterOperatorModal, setShowRegisterOperatorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const toast = useRef<Toast>(null);

  useEffect(() => { fetchBranchDetails(); }, [branchId]);

  const fetchBranchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<ApiResponse>(`/v1/branches/${branchId}`);
      if (response.data.success) {
        setBranch(response.data.data);
        onBranchLoad?.(response.data.data.name, response.data.data.institute_id);
      } else {
        setError('Failed to load branch details');
      }
    } catch (err: any) {
      console.error('Failed to fetch branch details:', err);
      setError(err.response?.data?.error || 'Failed to load branch details');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterOperatorSuccess = () => {
    setShowRegisterOperatorModal(false);
    fetchBranchDetails();
  };

  const handleEditBranchSuccess = () => {
    setShowEditModal(false);
    fetchBranchDetails();
    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Branch updated successfully', life: 3000 });
  };

  const handleResetPasswordSuccess = () => {
    toast.current?.show({
      severity: 'success',
      summary: 'Password Reset',
      detail: `Password for ${branch?.active_operator?.name} has been reset successfully`,
      life: 3000,
    });
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateString; }
  };

  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      return `${hour % 12 || 12}:${minutes} ${ampm}`;
    } catch { return timeString; }
  };

  const calculateLicenseDaysRemaining = (expiryDate: string) => {
    return Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: '#f5f5f5', borderRadius: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
          <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading branch details...</p>
        </div>
      </div>
    );
  }

  if (error || !branch) {
    return (
      <div>
        <Button icon="pi pi-arrow-left" label="Back to Branches" onClick={onBack} outlined style={{ marginBottom: '20px' }} />
        <Message severity="error" text={error || 'Branch not found'} />
      </div>
    );
  }

  const daysRemaining = calculateLicenseDaysRemaining(branch.license_expiry);
  const isLicenseExpiring = daysRemaining < 30 && daysRemaining > 0;
  const isLicenseExpired = daysRemaining <= 0;

  return (
    <div style={{ padding: '0' }}>
      <Toast ref={toast} position="top-right" />

      {/* ── Hero Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white',
        position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15,61,62,0.3)',
      }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212,241,126,0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '250px', height: '250px', background: 'rgba(212,241,126,0.08)', borderRadius: '50%', filter: 'blur(50px)' }} />

        <Button icon="pi pi-arrow-left" label="Back to Branches" onClick={onBack} text
          style={{ color: 'white', marginBottom: '10px', marginLeft: '-20px', fontSize: '0.9rem', position: 'relative', zIndex: 1 }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px', position: 'relative', zIndex: 1 }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212,241,126,0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Branch Details
            </div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>{branch.name}</h1>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              {branch.branch_code && (
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#d4f17e', fontWeight: '700' }}>
                  {branch.branch_code}
                </span>
              )}
              {branch.branch_type && (
                <span style={{ padding: '3px 10px', background: 'rgba(212,241,126,0.2)', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                  {getBranchTypeLabel(branch.branch_type)}
                </span>
              )}
            </div>

            <p style={{ margin: '0 0 8px 0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
              {[branch.village, branch.district, branch.state].filter(Boolean).join(', ')}
              {branch.pincode ? ` • PIN: ${branch.pincode}` : ''}
            </p>

            {(branch.email || branch.contact_no) && (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {branch.email && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)' }}>
                    <i className="pi pi-envelope" style={{ fontSize: '0.8rem', color: '#d4f17e' }} />
                    {branch.email}
                  </span>
                )}
                {branch.contact_no && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)' }}>
                    <i className="pi pi-phone" style={{ fontSize: '0.8rem', color: '#d4f17e' }} />
                    {branch.contact_no}
                  </span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {[
                { label: 'Capacity', value: branch.capacity },
                { label: 'Total PCs', value: branch.total_pc },
                { label: 'Hours', value: `${formatTime(branch.opening_time)} - ${formatTime(branch.closing_time)}` },
              ].map(stat => (
                <div key={stat.label} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</p>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#d4f17e' }}>{stat.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Tag
              value={branch.status.toUpperCase()}
              style={{ fontSize: '0.7rem', fontWeight: '600', padding: '5px 12px', background: branch.status === 'active' ? '#d4f17e' : '#ff6b6b', color: branch.status === 'active' ? '#0f3d3e' : 'white', border: 'none' }}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>
              ID: {branch.branch_id}
            </p>
          </div>
        </div>
      </div>

      {/* ── License Warning ── */}
      {(isLicenseExpiring || isLicenseExpired) && (
        <Message
          severity={isLicenseExpired ? 'error' : 'warn'}
          text={isLicenseExpired
            ? `⚠️ License expired ${Math.abs(daysRemaining)} days ago - Immediate action required`
            : `⏰ License expiring in ${daysRemaining} days - Please renew soon`}
          style={{ marginBottom: '20px', fontSize: '0.85rem', fontWeight: '500' }}
        />
      )}

      {/* ── Two Column Layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

        {/* ── Left Column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* License Information */}
          <InfoCard title="License Information" icon="pi pi-id-card" subtitle="Official license details and validity">
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>License Number</p>
              <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#0f3d3e', fontFamily: 'monospace', letterSpacing: '1px' }}>{branch.license_number}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Start Date</p>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#333' }}>{formatDate(branch.license_start)}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Expiry Date</p>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50' }}>{formatDate(branch.license_expiry)}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Days Remaining</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className={`pi ${isLicenseExpired ? 'pi-times-circle' : isLicenseExpiring ? 'pi-exclamation-triangle' : 'pi-check-circle'}`}
                    style={{ fontSize: '1rem', color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50' }} />
                  <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50' }}>
                    {daysRemaining > 0 ? daysRemaining : 'Expired'}
                  </p>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Course Enrollment Count — NEW */}
          <CourseEnrollmentSection branchId={branchId} />

          {/* Location Information */}
          <InfoCard title="Location Details" icon="pi pi-map-marker" subtitle="Branch address and regional info">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <LabelValue label="State"          value={branch.state    || '—'} />
              <LabelValue label="District"       value={branch.district || '—'} />
              <LabelValue label="Village / Area" value={branch.village  || '—'} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <LabelValue label="Branch Type" value={getBranchTypeLabel(branch.branch_type)} mono={false} />
            </div>

            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Full Address</p>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#333', lineHeight: '1.5', fontWeight: '500' }}>{branch.address}</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', fontWeight: '700', color: '#0f3d3e' }}>Pincode: {branch.pincode}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '12px', background: 'linear-gradient(135deg, rgba(15,61,62,0.05) 0%, rgba(26,85,85,0.05) 100%)', borderRadius: '10px' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase' }}>Latitude</p>
                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0f3d3e', fontFamily: 'monospace' }}>{branch.latitude ?? '—'}°</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase' }}>Longitude</p>
                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0f3d3e', fontFamily: 'monospace' }}>{branch.longitude ?? '—'}°</p>
              </div>
            </div>
          </InfoCard>

          {/* Contact Information */}
          {(branch.email || branch.contact_no) && (
            <InfoCard title="Contact Information" icon="pi pi-phone" subtitle="Branch email and contact number">
              <div style={{ display: 'grid', gridTemplateColumns: branch.email && branch.contact_no ? '1fr 1fr' : '1fr', gap: '12px' }}>
                {branch.email && (
                  <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '10px' }}>
                    <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Address</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-envelope" style={{ color: '#0f3d3e', fontSize: '0.9rem' }} />
                      <a href={`mailto:${branch.email}`} style={{ fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', textDecoration: 'none' }}>
                        {branch.email}
                      </a>
                    </div>
                  </div>
                )}
                {branch.contact_no && (
                  <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '10px' }}>
                    <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contact Number</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-phone" style={{ color: '#0f3d3e', fontSize: '0.9rem' }} />
                      <a href={`tel:${branch.contact_no}`} style={{ fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', textDecoration: 'none' }}>
                        {branch.contact_no}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>
          )}
        </div>

        {/* ── Right Column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Active Operator */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '700', color: '#0f3d3e' }}>Active Operator</h3>

            {branch.active_operator ? (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', borderRadius: '12px', marginBottom: '16px' }}>
                  <Avatar
                    label={branch.active_operator.name.charAt(0)}
                    size="xlarge"
                    style={{ backgroundColor: '#d4f17e', color: '#0f3d3e', width: '64px', height: '64px', fontSize: '1.6rem', fontWeight: '700', marginBottom: '12px' }}
                    shape="circle"
                  />
                  <p style={{ margin: '0 0 6px 0', fontSize: '1.1rem', fontWeight: '700', color: 'white', textAlign: 'center' }}>{branch.active_operator.name}</p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', textAlign: 'center', wordBreak: 'break-all' }}>{branch.active_operator.email}</p>
                  <Tag value={branch.active_operator.role.toUpperCase()} style={{ background: '#d4f17e', color: '#0f3d3e', fontSize: '0.7rem', padding: '5px 10px', fontWeight: '700' }} />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>User ID</p>
                  <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '600', color: '#0f3d3e', fontFamily: 'monospace', background: '#f8f9fa', padding: '8px 10px', borderRadius: '8px' }}>{branch.active_operator.user_id}</p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Assigned On</p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#333', fontWeight: '500' }}>{formatDate(branch.active_operator.assigned_at)}</p>
                </div>

                <button
                  onClick={() => setShowResetPasswordModal(true)}
                  style={{
                    width: '100%', padding: '10px', background: 'transparent',
                    border: '1.5px dashed #0f3d3e', borderRadius: '10px', color: '#0f3d3e',
                    fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '7px', transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderStyle = 'solid'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderStyle = 'dashed'; }}
                >
                  <i className="pi pi-lock" style={{ fontSize: '0.85rem' }} />
                  Reset Operator Password
                </button>
              </div>
            ) : (
              <div style={{ padding: '32px 16px', textAlign: 'center', background: '#f8f9fa', borderRadius: '10px' }}>
                <i className="pi pi-user-minus" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: '12px', display: 'block' }}></i>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>No operator assigned to this branch</p>
                <Button label="Register Operator" icon="pi pi-user-plus" onClick={() => setShowRegisterOperatorModal(true)}
                  style={{ background: '#0f3d3e', border: 'none', fontSize: '0.8rem', padding: '8px 16px' }} />
              </div>
            )}
          </div>

          {/* Metadata */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '700', color: '#0f3d3e' }}>Metadata</h3>

            {branch.branch_code && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Branch Code</p>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: '#0f3d3e', fontFamily: 'monospace', background: '#f8f9fa', padding: '8px 10px', borderRadius: '8px' }}>{branch.branch_code}</p>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Institute ID</p>
              <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '600', color: '#0f3d3e', fontFamily: 'monospace', background: '#f8f9fa', padding: '8px 10px', borderRadius: '8px', wordBreak: 'break-all' }}>{branch.institute_id}</p>
            </div>

            <div>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>Created At</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#333', fontWeight: '500' }}>{formatDate(branch.created_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button label="Edit Branch" icon="pi pi-pencil" onClick={() => setShowEditModal(true)}
          style={{ background: '#0f3d3e', border: 'none', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
        {!branch.active_operator && (
          <Button label="Register Operator" icon="pi pi-user-plus" onClick={() => setShowRegisterOperatorModal(true)} outlined
            style={{ color: '#0f3d3e', borderColor: '#0f3d3e', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
        )}
        {/* ← View Students button */}
        <Button
          label="View Students"
          icon="pi pi-users"
          onClick={() => onViewStudents?.()}
          style={{ background: '#1a5555', border: 'none', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600', color: 'white' }}
        />
        <Button label="View Reports" icon="pi pi-chart-line" outlined
          style={{ color: '#0f3d3e', borderColor: '#0f3d3e', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
        <Button label="Delete Branch" icon="pi pi-trash" severity="danger" outlined
          style={{ padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
      </div>

      {/* ── Modals ── */}
      <RegisterOperatorModal
        visible={showRegisterOperatorModal}
        onHide={() => setShowRegisterOperatorModal(false)}
        onSuccess={handleRegisterOperatorSuccess}
        toastRef={toast}
        preselectedBranchId={branch.branch_id}
        preselectedInstituteId={branch.institute_id}
      />

      <EditBranchModal
        visible={showEditModal}
        branch={branch}
        onHide={() => setShowEditModal(false)}
        onSuccess={handleEditBranchSuccess}
        toastRef={toast}
      />

      {branch.active_operator && (
        <AdminResetPasswordModal
          visible={showResetPasswordModal}
          onHide={() => setShowResetPasswordModal(false)}
          onSuccess={handleResetPasswordSuccess}
          userId={branch.active_operator.user_id}
          name={branch.active_operator.name}
          role="operator"
        />
      )}
    </div>
  );
};

// ── Small reusable sub-components ──────────────────────────────────────────────

const InfoCard: React.FC<{ title: string; icon: string; subtitle: string; children: React.ReactNode }> = ({ title, icon, subtitle, children }) => (
  <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={icon} style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
      </div>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>{title}</h2>
        <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#999' }}>{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

const LabelValue: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono = false }) => (
  <div>
    <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>{label}</p>
    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#333', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</p>
  </div>
);

export default BranchDetails;